import { useState, useContext, useEffect, useMemo } from "react";
import AdminLeloContext from "../../context/adminLelo/adminLeloContext";
import UserContext from "../../context/users/userContext";
import AuthContext from "../../context/auth/authContext";
import * as FaIcons from "react-icons/fa";
import { toast } from "react-toastify";
import axios from "axios";
import setAuthToken from "../../functions/auth_header";
import { getAppUrl, getLocationApiUrl } from "../../functions/getEnv";

const LeloFilterSidebar = ({ isOpen, onClose, onSessionSelect }) => {
  const adminLeloContext = useContext(AdminLeloContext);
  const userContext = useContext(UserContext);
  const authContext = useContext(AuthContext);

  const { user: authUser } = authContext;
  const { currentUser, user, charters, getCharters } = userContext;
  const {
    getLeloEntries,
    pageSize,
    setCurrentPage,
    ActiveFilter,
    SignedFilter,
    InactiveFilter,
    appliedFilters,
    startDate: contextStartDate,
    endDate: contextEndDate,
    setSession: contextSession,
  } = adminLeloContext;

  // Get active user
  const activeUser = authUser || currentUser || user;
  const isSuperAdmin = activeUser?.userStatus === "Super Admin";

  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);

  const formatDate = (date) => date.toISOString().slice(0, 10);
  const maxDate = formatDate(today);

  // Memoize context filters to avoid recalculation and ensure stable initial state
  const contextFilters = useMemo(() => {
    const status = appliedFilters?.status || "Active";

    // Look up charter name from charter ID if charters are loaded
    let charterName = "";
    let charterId = appliedFilters?.learningCenterCharter || "";

    // If we have a charter ID, look up its name
    if (charterId && charters && charters.length > 0) {
      const matchedCharter = charters.find(c => c._id === charterId);
      charterName = matchedCharter ? matchedCharter.name : "";
    }

    // Get learning center info
    const learningCenterId = appliedFilters?.learningCenterId || "";
    let learningCenterName = appliedFilters?.learningCenterName || "";

    return {
      statusActive: status === "Active",
      statusSigned: status === "Signed",
      statusInactive: status === "Inactive",
      charterName: charterName,
      charterId: charterId,
      learningCenterName: learningCenterName,
      learningCenterId: learningCenterId,
      session: contextSession || appliedFilters?.permaLink || "",
      sessionId: null,
      startDate: contextStartDate ? formatDate(new Date(contextStartDate)) : formatDate(oneMonthAgo),
      endDate: contextEndDate ? formatDate(new Date(contextEndDate)) : formatDate(today),
    };
  }, [appliedFilters, contextSession, contextStartDate, contextEndDate, charters]);

  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Initialize filters from memoized context filters
  const [filters, setFilters] = useState(contextFilters);

  const [dateValidationError, setDateValidationError] = useState("");
  const [availableCharters, setAvailableCharters] = useState([]);
  const [allSites, setAllSites] = useState([]); // Store all sites from API
  const [filteredLearningCenters, setFilteredLearningCenters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Sync filters with memoized context filters when sidebar opens
  // Also look up charter from site data if we have learningCenterId but no charter
  useEffect(() => {
    if (isOpen) {
      let updatedFilters = { ...contextFilters };

      // If we have a learning center ID but no charter, look it up from allSites
      if (contextFilters.learningCenterId && !contextFilters.charterName && allSites.length > 0 && charters && charters.length > 0) {
        const selectedSite = allSites.find(site => site._id === contextFilters.learningCenterId);
        if (selectedSite && selectedSite.charter) {
          // Get charter ID from site
          const siteCharterId = typeof selectedSite.charter === 'object' ? selectedSite.charter._id : selectedSite.charter;
          // Look up charter name
          const matchedCharter = charters.find(c => c._id === siteCharterId);
          if (matchedCharter) {
            updatedFilters.charterId = siteCharterId;
            updatedFilters.charterName = matchedCharter.name;
          }
          // Also set learning center name if not set
          if (!updatedFilters.learningCenterName) {
            updatedFilters.learningCenterName = selectedSite.name;
          }
        }
      }

      setFilters(updatedFilters);
    }
  }, [isOpen, contextFilters, allSites, charters]);

  // Fetch all data once when component mounts
  useEffect(() => {
    if (isDataLoaded) return;

    const loadData = async () => {
      try {
        const appUrl = getAppUrl(import.meta.env.VITE_NODE_ENV);
        const locationApiUrl = getLocationApiUrl(import.meta.env.VITE_NODE_ENV);


        // Set authentication token
        if (localStorage.token) {
          setAuthToken(localStorage.token);
        }

        // Fetch sessions for pre-registration
        const sessionsResponse = await axios.get(`${appUrl}/api/sessions`);
        setSessions(sessionsResponse.data);

        // Fetch sites from locations API with proper auth headers
        try {
          const sitesResponse = await axios.get(`${locationApiUrl}/sitessimp`, {
            headers: {
              'x-auth-token': localStorage.token
            }
          });
          const sitesData = sitesResponse.data.data || [];
          setAllSites(sitesData);
        } catch (siteError) {
          console.error('Error fetching sites:', siteError.response || siteError);
          toast.error('Failed to load learning centers');
        }

        // Load charters if needed (this will load from locations API via UserContext)
        if (getCharters && (!charters || charters.length === 0)) {
          getCharters();
        }

        setIsDataLoaded(true);
      } catch (error) {
        console.error("Error loading filter data:", error);
        toast.error("Failed to load filter data");
      }
    };

    loadData();
  }, []);

  // Update available charters when charters load
  useEffect(() => {
    if (charters ) {
      const charterNames = charters.map(c => c.name).filter(Boolean).sort();
      setAvailableCharters([...new Set(charterNames)]);
    }
  }, [charters]);

  // Filter learning centers based on selected charter
  useEffect(() => {
    console.log('Filter useEffect triggered:', {
      charterName: filters.charterName,
      allSitesCount: allSites.length,
      chartersCount: charters?.length
    });

    if (filters.charterName && allSites.length > 0 && charters && charters.length > 0) {
      // Find the selected charter object
      const selectedCharter = charters.find(c => c.name === filters.charterName);

      if (selectedCharter) {
        // Filter sites that belong to this charter
        // Compare both _id and name to handle different data structures
        const centersForCharter = allSites
          .filter(site => {
            if (!site.charter) return false;

            // Check if charter is an object or just an ID
            const siteCharterId = typeof site.charter === 'object' ? site.charter._id : site.charter;
            const siteCharterName = typeof site.charter === 'object' ? site.charter.name : null;

            // Match by ID or by name
            const matchesById = siteCharterId === selectedCharter._id ||
                               siteCharterId?.toString() === selectedCharter._id?.toString();
            const matchesByName = siteCharterName === selectedCharter.name;

            console.log('Site:', site.name, {
              siteCharterId,
              siteCharterName,
              selectedCharterId: selectedCharter._id,
              selectedCharterName: selectedCharter.name,
              matchesById,
              matchesByName
            });

            return matchesById || matchesByName;
          })
          .map(site => ({
            id: site._id,
            name: site.name,
            displayName: site.name, // Use name as displayName
            charter: typeof site.charter === 'object' ? site.charter.name : filters.charterName
          }))
          .sort((a, b) => a.displayName.localeCompare(b.displayName));

        setFilteredLearningCenters(centersForCharter);
      } else {
        console.log('Selected charter not found in charters array');
        setFilteredLearningCenters([]);
      }
     } 
  }, [filters.charterName, allSites, charters]);

  // Date validation function
  const validateDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) {
      setDateValidationError("");
      return true;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const todayDate = new Date();

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    todayDate.setHours(0, 0, 0, 0);

    if (start > todayDate) {
      setDateValidationError("Start date cannot be in the future");
      return false;
    }

    if (end > todayDate) {
      setDateValidationError("End date cannot be in the future");
      return false;
    }

    if (start > end) {
      setDateValidationError("Start date must be less than or equal to end date");
      return false;
    }

    setDateValidationError("");
    return true;
  };

  const handleCheckboxChange = (field) => {
    // Only one status can be selected at a time
    setFilters((prev) => ({
      ...prev,
      statusActive: field === "statusActive" ? !prev.statusActive : false,
      statusSigned: field === "statusSigned" ? !prev.statusSigned : false,
      statusInactive: field === "statusInactive" ? !prev.statusInactive : false,
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const newFilters = {
        ...prev,
        [field]: value,
      };

      // Clear learning center when charter changes
      if (field === "charterName") {
        newFilters.learningCenterName = "";
        newFilters.learningCenterId = "";
        // Store charter ID when charter is selected
        const selectedCharter = charters?.find(c => c.name === value);
        newFilters.charterId = selectedCharter ? selectedCharter._id : "";
      }

      if (field === "startDate" || field === "endDate") {
        validateDateRange(
          field === "startDate" ? value : prev.startDate,
          field === "endDate" ? value : prev.endDate
        );
      }

      return newFilters;
    });
  };

  // Handle learning center selection - store both ID and name
  const handleLearningCenterChange = (e) => {
    const selectedSiteId = e.target.value;
    const selectedSite = filteredLearningCenters.find(site => site.id === selectedSiteId);

    setFilters((prev) => ({
      ...prev,
      learningCenterId: selectedSiteId,
      learningCenterName: selectedSite ? selectedSite.displayName : "",
    }));
  };

  const handleSessionChange = (e) => {
    const selectedPermalink = e.target.value;
    const selectedSessionObj = sessions.find(session => session.permalink === selectedPermalink);

    setFilters((prev) => ({
      ...prev,
      session: selectedPermalink,
      sessionId: selectedSessionObj ? selectedSessionObj._id : null,
    }));
  };

  const fetchFilteredDataFromBackend = async () => {
    try {
      if (!validateDateRange(filters.startDate, filters.endDate)) {
        return;
      }

      setIsLoading(true);

      const backendFilters = {
        page: 1,
        pageSize: pageSize || 300,
      };

      // Get selected status
      let status = "Active"; // default
      if (filters.statusActive) status = "Active";
      else if (filters.statusSigned) status = "Signed";
      else if (filters.statusInactive) status = "Inactive";

      backendFilters.status = status;

      // Apply context status filter
      if (status === "Active") ActiveFilter();
      else if (status === "Signed") SignedFilter();
      else if (status === "Inactive") InactiveFilter();

      // Use site ID for API call if available, otherwise use charter ID
      // Always send charter ID along with learning center ID for proper filter restoration
      if (filters.learningCenterId) {
        backendFilters.learningCenterId = filters.learningCenterId;
        // Also send charter ID so it can be restored when reopening filters
        if (filters.charterId) {
          backendFilters.learningCenterCharter = filters.charterId;
        }
      } else if (filters.charterId) {
        backendFilters.learningCenterCharter = filters.charterId;
      }

      if (filters.session) backendFilters.permaLink = filters.session;

      if (filters.startDate && filters.endDate) {
        const localStart = new Date(filters.startDate);
        localStart.setHours(0, 0, 0, 0);
        const localEnd = new Date(filters.endDate);
        localEnd.setHours(23, 59, 59, 999);
        backendFilters.startDate = localStart.toISOString();
        backendFilters.endDate = localEnd.toISOString();
      }

      setCurrentPage(1);
      await getLeloEntries(backendFilters);

      // Notify parent of session selection for pre-registration
      if (onSessionSelect && filters.session) {
        onSessionSelect(filters.session, filters.sessionId);
      }

      toast.success("Filters applied successfully");
      onClose();
    } catch (error) {
      console.error("Error applying filters:", error);
      toast.error("Failed to apply filters");
    } finally {
      setIsLoading(false);
    }
  };

  const clearAllFilters = () => {
    setFilters({
      statusActive: true,
      statusSigned: false,
      statusInactive: false,
      charterName: "",
      charterId: "",
      learningCenterName: "",
      learningCenterId: "",
      session: "",
      sessionId: null,
      startDate: formatDate(oneMonthAgo),
      endDate: formatDate(today),
    });
    setDateValidationError("");
  };

  // Handle closing the sidebar - revert to context filters
  const handleClose = () => {
    // Revert to memoized context filters on cancel
    setFilters(contextFilters);
    setDateValidationError("");
    onClose();
  };

  // Don't render anything if not open (for performance)
  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="filter-sidebar-overlay" onClick={handleClose}></div>

      {/* Sidebar */}
      <div className={`filter-sidebar ${isOpen ? "open" : ""}`}>
        <div className="filter-sidebar-header">
          <h3>Filters</h3>
          <button className="close-btn" onClick={handleClose}>
            <FaIcons.FaTimes />
          </button>
        </div>

        <div className="filter-sidebar-content">
          {/* Status Filter with Checkboxes */}
          <div className="filter-section">
            <label className="filter-label">Status</label>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={filters.statusActive}
                  onChange={() => handleCheckboxChange("statusActive")}
                />
                <span>Active</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={filters.statusSigned}
                  onChange={() => handleCheckboxChange("statusSigned")}
                />
                <span>Signed</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={filters.statusInactive}
                  onChange={() => handleCheckboxChange("statusInactive")}
                />
                <span>Inactive</span>
              </label>
            </div>
          </div>

          {/* Sessions Filter (Dropdown) */}
          <div className="filter-section">
            <label className="filter-label">Session</label>
            <select
              value={filters.session}
              onChange={handleSessionChange}
              className="filter-select"
            >
              <option value="">All Sessions</option>
              {sessions
                .filter((session) => session.status === "Active")
                .map((session) => (
                  <option key={session._id} value={session.permalink}>
                    {session.name}
                  </option>
                ))}
            </select>
          </div>

          {/* Date Range Filter */}
          <div className="filter-section">
            <label className="filter-label">Date Range</label>
            <div className="date-range-inputs">
              <div className="date-input-group">
                <label className="sub-label">Start Date</label>
                <input
                  type="date"
                  className="filter-input"
                  value={filters.startDate}
                  max={maxDate}
                  onChange={(e) => handleFilterChange("startDate", e.target.value)}
                />
              </div>
              <div className="date-input-group">
                <label className="sub-label">End Date</label>
                <input
                  type="date"
                  className="filter-input"
                  value={filters.endDate}
                  max={maxDate}
                  min={filters.startDate || undefined}
                  onChange={(e) => handleFilterChange("endDate", e.target.value)}
                />
              </div>
            </div>
            {dateValidationError && (
              <div className="error-message">
                <FaIcons.FaExclamationCircle />
                {dateValidationError}
              </div>
            )}
          </div>

          {/* Charter Filter */}
          <div className="filter-section">
            <label className="filter-label">Charter</label>
            <select
              value={filters.charterName}
              onChange={(e) => handleFilterChange("charterName", e.target.value)}
              className="filter-select"
            >
              <option value="">All Charters</option>
              {availableCharters.map((charter, index) => (
                <option key={index} value={charter}>
                  {charter}
                </option>
              ))}
            </select>
          </div>

          {/* Learning Center Filter (Site) */}
          <div className="filter-section">
            <label className="filter-label">Learning Center</label>
            <select
              value={filters.learningCenterId}
              onChange={handleLearningCenterChange}
              className="filter-select"
              disabled={!filters.charterName}
            >
              <option value="">All Learning Centers</option>
              {filteredLearningCenters.map((center) => (
                <option key={center.id} value={center.id} title={center.displayName}>
                  {center.displayName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="filter-sidebar-footer">
          <button className="clear-all-btn" onClick={clearAllFilters}>
            Clear All
          </button>
          <button
            className="apply-filters-btn"
            onClick={fetchFilteredDataFromBackend}
            disabled={!!dateValidationError || isLoading}
          >
            {isLoading ? "Applying..." : "Apply Filters"}
          </button>
        </div>
      </div>
    </>
  );
};

export default LeloFilterSidebar;
